import { useEffect, useMemo } from 'react';
import { MantineReactTable, useMantineReactTable } from 'mantine-react-table';
import { MRT_Localization_PT_BR } from 'mantine-react-table/locales/pt-BR';
import { apiInstance } from './../../services/api_service';
import { Box, Text, Tooltip, Menu } from '@mantine/core';
import { useState } from 'react';
import { getRerenrollmentStatus } from '../../utils/utils';
import StudentDetailModal from '../modals/StudentDetailModal';
import StudentLogs from '../modals/StudentLogs';

// Local storage functions for page size persistence
const STORAGE_KEY = 'studentsTable_pageSize';

const getStoredPageSize = (defaultSize = 15) => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = parseInt(stored, 10);
      // Validate that it's a reasonable page size
      if (parsed >= 5 && parsed <= 100) {
        return parsed;
      }
    }
  } catch (error) {
    console.warn('Failed to read page size from localStorage:', error);
  }
  return defaultSize;
};

const setStoredPageSize = (pageSize) => {
  try {
    localStorage.setItem(STORAGE_KEY, pageSize.toString());
  } catch (error) {
    console.warn('Failed to save page size to localStorage:', error);
  }
};

const getSegmentLabel = (segmentValue) => {
  switch (segmentValue) {
    case 'preschool':
      return 'Infantil';
    case 'elementary-I':
      return 'Fundamental I';
    case 'elementary-II':
      return 'Fundamental II';
    case 'high-school':
      return 'Médio';
    default:
      return segmentValue;
  }
}

const getStudentStatus = (status) => {
  switch (status) {
    case 'active':
      return 'Ativo';
    case 'inactive':
      return 'Inativo';
    case 'not-reenrolled':
      return 'Não matriculado';
    default:
      return status;
  }
}

const classDisplayBox = (value, color, label) => {
  if (!label) {
    label = value;
  } 
  
  const boxElement = (
    <Box
      sx={(theme) => {
        let bgColor = theme.colors.red[6];
        if (color === 'red') {
          bgColor = theme.colors.red[6];
        } else if (color === 'green') {
          bgColor = theme.colors.green[6];
        } else if (color === 'blue') {
          bgColor = theme.colors.blue[6];
        } else if (color === 'yellow') {
          bgColor = theme.colors.yellow[6];
        } if (color === 'indigo') {
          bgColor = theme.colors.indigo[3];
        } else if (color === 'gray') {
          bgColor = theme.colors.gray[6];
        }
        return {
          backgroundColor: bgColor,
          borderRadius: '4px',
          color: '#fff',
          paddingTop: '2px',
          paddingBottom: '2px',
          paddingLeft: '4px',
          paddingRight: '4px',
          marginLeft: '4px',
        }
      }}
    >
      {value}
    </Box>
  );

  // Only show tooltip for gray boxes (+X values)
  if (color === 'gray') {
    return (
      <Tooltip label={label} position="left">
        {boxElement}
      </Tooltip>
    );
  }

  return boxElement;
}

const StudentsTable = ({tableConfig}) => {
  //data and fetching state
  const [data, setData] = useState([]);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [rowCount, setRowCount] = useState(0);

  //modal state
  const [modalOpened, setModalOpened] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  
  //logs modal state
  const [logsModalOpened, setLogsModalOpened] = useState(false);
  const [selectedStudentForLogs, setSelectedStudentForLogs] = useState(null);

  //table state
  const [columnFilters, setColumnFilters] = useState(tableConfig.initialColumnFilters || []);
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: getStoredPageSize(tableConfig.pageSize || 15),
  });

  // Instances
  const [tableInstance, setTableInstance] = useState(null);
  const [classFilterOptions, setClassFilterOptions] = useState([]);
  const [lastReenrollmentLogCreatedByFilterOptions, setLastReenrollmentLogCreatedByFilterOptions] = useState([]);

  const globalFilterChange = async (filterValue) => {
    if(filterValue === undefined && !globalFilter) {
      return;
    }
    setPagination({ ...pagination, pageIndex: 0 });
    setGlobalFilter(filterValue);
    return;
  }

  const columnFiltersChange = async (columnFilters) => {
    setPagination({ ...pagination, pageIndex: 0 });
    setColumnFilters(columnFilters);
  };

  const fetchClasses = async () => {
    if (classFilterOptions.length === 0) {
      try {
        const response = await apiInstance.get(`students/student-classes/?year=${tableConfig.year}&page_size=100&page=1`);
        let classes = response.data.results;
        let classesOptions = [];
        classes.forEach((studentClass) => {
          const classId = studentClass.id;
          const className = studentClass.name;
          classesOptions.push({ value: classId, label: className });
        });
        setClassFilterOptions(classesOptions);
        
      } catch (error) {
        console.error(error);
      }
    }
  };

  const fetchLastReenrollmentLogCreatedByFilterOptions = async () => {
    if (lastReenrollmentLogCreatedByFilterOptions.length === 0) {
      try {
        const response = await apiInstance.get(`students/waiting-reenrollment/log-created-by-users/`);
        let created_by_users = response.data;
        let options = [{ value: '0', label: 'Nenhum comentário' }];
        created_by_users.forEach((user) => {
          const userId = user.id;
          const userName = `${user.first_name} ${user.last_name}`;
          options.push({ value: userId, label: userName });
        });
        setLastReenrollmentLogCreatedByFilterOptions(options);
      } catch (error) {
        console.error(error);
      }
    }
  };

  const fetchData = async () => {
    if (!data.length) {
      setIsLoading(true);
    } else {
      setIsRefetching(true);
    }

    if(tableInstance) {
      tableInstance.resetExpanded();
    }

    const params = {
      page_size: pagination.pageSize,
      page: pagination.pageIndex + 1,
    }
    if (tableConfig && tableConfig.baseQueryParams) {
      Object.keys(tableConfig.baseQueryParams).forEach((key) => {
        params[key] = tableConfig.baseQueryParams[key];
      });
    }

    columnFilters.forEach((filter, index) => {
      switch (filter.id) {
        case 'name':
          params[`name__icontains`] = filter.value;
          break;
        case 'external_id':
          params[`external_id__icontains`] = filter.value;
          break;
        case 'enrollments':
          params[`enrollment__student_class__pk`] = filter.value;
          break;
        case 'segment':
          params[`enrollment__student_class__segment__in`] = filter.value;
          break;
        case 'status':
          params[`status`] = filter.value;
          break;
        case 'last_log_created_by_user_name':
          params[`last_reenrollment_log_created_by_pk`] = filter.value;
          break;
        case 'reenrollment_status':
          params[`reenrollment_status__status`] = filter.value;
          break;
        default:
          break;
      }
    });
    if (globalFilter) {
      params[`search`] = globalFilter;
    }
    
    // Handle sorting
    if (sorting.length > 0) {
      const sortField = sorting[0].id;
      const sortDirection = sorting[0].desc ? '-' : '';
      
      switch (sortField) {
        case 'last_log_created_days_ago':
          params['ordering'] = `${sortDirection}last_log_created_days_ago`;
          break;
        default:
          // Handle other sorting fields if needed
          break;
      }
    }
    
    const queryString = new URLSearchParams(params).toString();
    try {
      const response = await apiInstance.get(`${tableConfig.studentsPath}?${queryString}`);
      setData(response.data.results);
      setRowCount(response.data.count);
      await fetchClasses();

      if (tableConfig.columns && tableConfig.columns.last_reenrollment_log === true) {
        await fetchLastReenrollmentLogCreatedByFilterOptions();
      }
      
    } catch (error) {
      setIsError(true);
      console.error(error);
      return;
    }
    setIsError(false);
    setIsLoading(false);
    setIsRefetching(false);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    columnFilters, //refetch when column filters change
    globalFilter, //refetch when global filter changes
    pagination.pageIndex, //refetch when page index changes
    pagination.pageSize, //refetch when page size changes
    sorting, //refetch when sorting changes
  ]);

  const handleReenrollmentStatusUpdate = (studentId, newStatus) => {
    setData(currentData => 
      currentData.map(row => {
        if (row.id === studentId) {
          return {
            ...row,
            reenrollment_status: {
              ...row.reenrollment_status,
              status: newStatus
            }
          };
        }
        return row;
      })
    );
  };

  const handleLogUpdate = (studentId, userName) => {
    setData(currentData => 
      currentData.map(row => {
        if (row.id === studentId) {
          return {
            ...row,
            last_log_created_by_user_name: userName,
            last_log_created_days_ago: 0
          };
        }
        return row;
      })
    );
  };

  const handleStudentNameClick = (row) => {
    setSelectedRow(row);
    setModalOpened(true);
  };

  const handleModalClose = () => {
    setModalOpened(false);
    setSelectedRow(null);
  };

  const handleViewComments = (row) => {
    setSelectedStudentForLogs({
      id: row.original.id,
      name: row.original.name
    });
    setLogsModalOpened(true);
  };

  const handleLogsModalClose = () => {
    setLogsModalOpened(false);
    setSelectedStudentForLogs(null);
  };

  const handlePaginationChange = (updaterOrValue) => {
    setPagination((prevPagination) => {
      let newPagination;
      
      if (typeof updaterOrValue === 'function') {
        newPagination = updaterOrValue(prevPagination);
      } else {
        newPagination = updaterOrValue;
      }
      
      // Save page size to localStorage when it changes
      if (newPagination.pageSize !== prevPagination.pageSize) {
        setStoredPageSize(newPagination.pageSize);
      }
      
      return newPagination;
    });
  };

  const columns = useMemo(
    () => {
      let columnKeys = [
        {
          accessorKey: 'external_id',
          header: 'Código',
          size: 25,
          // maxSize: 25,
          enableSorting: false,
          mantineFilterTextInputProps: {
            placeholder: 'Filtrar',
          },
        },
        {
          accessorKey: 'name',
          header: 'Nome',
          size: 50,
          maxSize: 50,
          enableSorting: false,
          mantineFilterTextInputProps: {
            placeholder: 'Filtrar',
          },
          Cell: ({ cell }) => {
            const studentName = cell.getValue();
            
            return (
              <Tooltip 
                label="Clique para ver detalhes do estudante"
                position={cell.row.index === 0 ? "left" : "top"}
                withArrow
                zIndex={99999}
                offset={5}
              >
                <Text
                  sx={{
                    cursor: 'pointer',
                    color: 'inherit',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      color: '#2563eb',
                      backgroundColor: '#f0f9ff',
                      borderRadius: '4px',
                      padding: '2px 4px',
                      transform: 'translateY(-1px)',
                    }
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStudentNameClick(cell.row);
                  }}
                >
                  {studentName}
                </Text>
              </Tooltip>
            );
          },
        },
        {
          accessorKey: 'enrollments',
          header: 'Turmas',
          filterVariant: 'select',
          mantineFilterMultiSelectProps: {
            data: classFilterOptions,
          },
          size: 35,
          maxSize: 35,
          enableSorting: false,
          Cell: ({ cell }) => {
            let row = cell.row.original;
            let student = row;
            let enrollments = student.enrollments;
            let boxes = [];
            let totalPlus = 0;
            let totalPlusValues = [];
            let expectedEnrollmentsStatus = ['active', 'pre-enrollment'];
            
            enrollments = enrollments.filter((enrollment) => {
              // if student is active, only show expected enrollments status
              if (student.status === 'active' && !expectedEnrollmentsStatus.includes(enrollment.status)) {
                return false;
              }
              return true;
            });

            if (enrollments.length === 0) {
              return classDisplayBox('Nenhuma turma', 'gray');
            }

            for (let i = 0; i < enrollments.length; i++) {
              let className = enrollments[i].student_class.name;
              if (i === 0) {
                boxes.push(classDisplayBox(className));
              } else {
                totalPlus += 1;
                totalPlusValues.push(className);
              }
            }
            if (totalPlus > 0) {
              boxes.push(classDisplayBox(`+${totalPlus}`, 'gray', totalPlusValues.join(', ')));
            }
            return (boxes.map((box) => box));
          }
        },
        {
          accessorKey: 'segment',
          header: 'Segmento',
          filterVariant: 'select',
          mantineFilterMultiSelectProps: {
            data: [
              { value: 'preschool', label: 'Infantil' },
              { value: 'elementary-I', label: 'Fundamental I' },
              { value: 'elementary-II', label: 'Fundamental II' },
              { value: 'high-school', label: 'Médio' },
            ],
          },
          size: 25,
          maxSize: 25,
          enableSorting: false,
          Cell: ({ cell }) => {
            let row = cell.row.original;
            let student = row;
            let enrollments = student.enrollments;
            let boxes = [];
            let totalPlus = 0;
            let totalPlusValues = [];
            
            let expectedEnrollmentsStatus = ['active', 'pre-enrollment'];
            enrollments = enrollments.filter((enrollment) => {
              // if student is active, only show expected enrollments status
              if (student.status === 'active' && !expectedEnrollmentsStatus.includes(enrollment.status)) {
                return false;
              }
              return true;
            });

            if (enrollments.length === 0) {
              return classDisplayBox('Nenhum segmento', 'gray');
            }

            let segmentControl = {}

            for (let i = 0; i < enrollments.length; i++) {
              let segment = enrollments[i].student_class.segment;
              let segmentLabel = getSegmentLabel(segment);
              segmentControl[segment] = true;
              if (i === 0) {
                boxes.push(classDisplayBox(segmentLabel, "yellow"));
              } else if(!segmentControl[segment]) {
                totalPlus += 1;
                totalPlusValues.push(segmentLabel);
                segmentControl[segment] = true;
              }
            }
            if (totalPlus > 0) {
              boxes.push(classDisplayBox(`+${totalPlus}`, 'gray', totalPlusValues.join(', ')));
            }
            return (boxes.map((box) => box));
          }
        },
      ];
      if (tableConfig && tableConfig.columns && tableConfig.columns.status === true) {
        columnKeys.push({
          accessorKey: 'status',
          header: 'Situação',
          filterVariant: 'select',
          filterFn: 'equals',
          mantineFilterSelectProps: {
            data: [{
              value: 'active',
              label: getStudentStatus('active')
            },
            {
              value: 'inactive',
              label: getStudentStatus('inactive')
            },
            {
              value: 'not-reenrolled',
              label: getStudentStatus('not-reenrolled')
            }],
          },
          size: 22,
          maxSize: 22,
          enableSorting: false,
          Cell: ({ cell }) => {
            let value = cell.getValue();
            return getStudentStatus(value);
          }
        });
      }
      if (tableConfig && tableConfig.columns && tableConfig.columns.reenrollment_status === true) {
        columnKeys.push({
          accessorKey: 'reenrollment_status',
          header: 'Rematrícula',
          filterVariant: 'select',
          filterFn: 'equals',
          mantineFilterSelectProps: {
            data: [
              { value: 'not-done', label: getRerenrollmentStatus('not-done') },
              { value: 'will-do', label: getRerenrollmentStatus('will-do') },
              { value: 'will-not-do', label: getRerenrollmentStatus('will-not-do') },
              { value: 'maybe', label: getRerenrollmentStatus('maybe') },
              { value: 'under-negotiation', label: getRerenrollmentStatus('under-negotiation') },
            ],
          },
          editVariant: 'select',
          mantineEditSelectProps: {
            data: ([{
              value: 'Alabama',
              label: 'AL'
            }, {
              value: 'Alaska',
              label: 'AK'
            }])
          },
          size: 25,
          maxSize: 25,
          enableSorting: false,
          Cell: ({ cell }) => {
            let row = cell.row.original;
            if (!row.reenrollment_status) {
              return "Não realizada";
            }
            return getRerenrollmentStatus(row.reenrollment_status.status);
          }
        });
      }
      if (tableConfig && tableConfig.columns && tableConfig.columns.last_reenrollment_log === true) {
        columnKeys.push({
          accessorKey: 'last_log_created_by_user_name',
          header: 'Comentado por',
          filterVariant: 'select',
          filterFn: 'equals',
          mantineFilterSelectProps: {
            data: lastReenrollmentLogCreatedByFilterOptions,
          },
          size: 28,
          maxSize: 28,
          enableSorting: false,
          Cell: ({ cell }) => {
            let row = cell.row.original;
            const userName = row.last_log_created_by_user_name || "Nenhum comentário";
            
            // Determine color based on comment status
            let textColor = 'inherit';
            if (userName === "Nenhum comentário") {
              textColor = '#dc2626'; // Weak red for no comments
            } else if (row.last_log_created_days_ago !== null && row.last_log_created_days_ago !== undefined) {
              if (row.last_log_created_days_ago >= 14) {
                textColor = '#dc2626'; // Red for 14+ days
              } else if (row.last_log_created_days_ago >= 7) {
                textColor = '#eab308'; // Strong yellow for 7+ days
              }
            }
            
            return (
              <Tooltip 
                label="Clique para ver comentários"
                position={cell.row.index === 0 ? "left" : "top"}
                withArrow
                zIndex={99999}
                offset={5}
              >
                <Text
                  sx={{
                    cursor: 'pointer',
                    color: textColor,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      color: '#2563eb',
                      backgroundColor: '#f0f9ff',
                      borderRadius: '4px',
                      padding: '2px 4px',
                      transform: 'translateY(-1px)',
                    }
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewComments(cell.row);
                  }}
                >
                  {userName}
                </Text>
              </Tooltip>
            );
          }
        });
      }
      if (tableConfig && tableConfig.columns && tableConfig.columns.last_log_created_days_ago === true) {
        columnKeys.push({
          accessorKey: 'last_log_created_days_ago',
          header: 'Comentado há',
          enableColumnFilter: false,
          enableSorting: true,
          size: 20,
          maxSize: 20,
          Cell: ({ cell }) => {
            let row = cell.row.original;
            const daysAgo = row.last_log_created_days_ago;
            const hasLog = daysAgo !== null && daysAgo !== undefined;
            const displayText = hasLog ? `${daysAgo} dias` : "-";
            
            // Determine color based on comment status
            let textColor = 'inherit';
            if (!hasLog) {
              textColor = '#dc2626'; // Weak red for no comments
            } else if (daysAgo >= 14) {
              textColor = '#dc2626'; // Red for 14+ days
            } else if (daysAgo >= 7) {
              textColor = '#eab308'; // Strong yellow for 7+ days
            }
            
            return (
              <Tooltip 
                label="Clique para ver comentários"
                position={cell.row.index === 0 ? "left" : "top"}
                withArrow
                zIndex={99999}
                offset={5}
              >
                <Text
                  sx={{
                    cursor: 'pointer',
                    color: textColor,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      color: '#2563eb',
                      backgroundColor: '#f0f9ff',
                      borderRadius: '4px',
                      padding: '2px 4px',
                      transform: 'translateY(-1px)',
                    }
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewComments(cell.row);
                  }}
                >
                  {displayText}
                </Text>
              </Tooltip>
            );
          }
        });
      }
      return columnKeys
    },[
      classFilterOptions, //refetch when classes options change
      lastReenrollmentLogCreatedByFilterOptions, //refetch when last reenrollment log created by options change
      tableConfig,
    ],
  );

  const table = useMantineReactTable({
    renderTopToolbarCustomActions: ({ table }) => {
      setTableInstance(table);
      return (
        <Text
          size={'md'}
          weight={700}
          transform="uppercase"
          color="black"
        >{tableConfig.title}
        </Text>
      );
    },
    columns,
    data,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableRowSelection: false,
    enableColumnActions: false,
    layoutMode: 'grid',
    getRowId: (row) => {
      return row.id;
    },
    initialState: {
      density: 'xs', 
      showColumnFilters: true,
      columnVisibility: tableConfig.columnVisibility || {},
    },
    manualPagination: true,
    enableSorting: true,
    manualSorting: true,
    localization: MRT_Localization_PT_BR,
    // Density
    enableDensityToggle: false,
    // Search
    enableGlobalFilter: false,
    // Filter
    manualFiltering: true,
    enableFilterMatchHighlighting: true,
    columnFilterDisplayMode: 'subheader',
    rowCount,
    onColumnFiltersChange: columnFiltersChange,
    onGlobalFilterChange: globalFilterChange,
    onPaginationChange: handlePaginationChange,
    onSortingChange: setSorting,
    autoResetPageIndex: false,
    state: {
      columnFilters,
      globalFilter,
      isLoading,
      pagination,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      sorting,
    },
    displayColumnDefOptions: {
      'mrt-row-expand': {
        header: '',
        size: 5,
        maxSize: 5,
      },
      'mrt-row-actions': {
        header: '',
        size: 3,
        maxSize: 3,
      },
    },
    mantineFilterTextInputProps: {
      disabled: false,
      placeholder: 'Filtrar',
    },
    mantineToolbarAlertBannerProps: isError
      ? { color: 'red', children: 'Falha ao carregar dados' }
      : undefined,
    mantinePaperProps: {
      sx: {
        height: '100%',
        border: '0px solid !important',
        boxShadow: '0px 0px 0px 0px !important',
      }
    },
    mantineTableProps: {
      sx: {
        // Force detail panel to use full width
        '& tbody tr[data-expanded="true"] + tr td': {
          width: '100% !important',
          maxWidth: '100% !important',
          padding: '0 !important',
        },
        '& tbody tr[data-expanded="true"] + tr': {
          width: '100% !important',
        },
        // Override any width restrictions on detail panel cells
        '& .mantine-Table-td[data-column="mrt-row-expand"]': {
          width: '100% !important',
          maxWidth: '100% !important',
        }
      }
    },
    mantineTopToolbarProps: {
      sx: {
        
      }
    },
    mantineTableHeadProps: {
      sx: {
        
      }
    },
    mantineTableContainerProps: ({table}) => ({
      sx: {
        height: `calc(100% - ${table.refs.topToolbarRef.current?.offsetHeight}px - ${table.refs.bottomToolbarRef.current?.offsetHeight}px)`,
      }
    }),
    mantineTableBodyCellProps: ({ row }) => {
      const tags = row.original.tags;
      let bgColor = "#ffffff"
      if(tags && tags.length > 0 && tableConfig.context === 'reenrollment') {
        bgColor = '#feffd2';
      }
      return {
        sx: {
          backgroundColor: bgColor,
        },
      }
    },
    mantineTableBodyRowProps: ({ row }) => ({
      sx: {
        cursor: 'default',
        transition: 'all 0.2s ease',
        "@media (hover: hover)": {
          "[data-hover] > tbody > tr:hover": {
            backgroundColor: "#f8f9fa !important",
          },
        },
        // Override detail panel restrictions
        '& td[data-index="mrt-row-expand"]': {
          width: '100% !important',
          maxWidth: '100% !important',
        },
      }
    }),
    mantineDetailPanelProps: {
      sx: {
        width: '100% !important',
        maxWidth: '100% !important',
        border: '0px !important',
        '& > div': {
          width: '100% !important',
          maxWidth: '100% !important',
        }
      }
    },
    // Actions
    enableRowActions: tableConfig.enableRowActions || false,
    // enableEditing: true,
    // editDisplayMode: 'cell',
    positionActionsColumn: 'first',
    renderRowActionMenuItems: ({ row }) => {
      let tags = row.original.tags;
      let actionChoices = [];
      
      // Add "Ver detalhes do estudante" action for all contexts
      actionChoices.push({
        label: 'Ver detalhes do estudante',
        onClick: () => handleStudentNameClick(row)
      });
      
      // Add "Ver comentários" action for all contexts
      actionChoices.push({
        label: 'Ver comentários',
        onClick: () => handleViewComments(row)
      });
      
      if(tags.length === 0 && tableConfig.context === 'reenrollment') {
        actionChoices.push({
          label: 'Adicionar destaque',
          onClick: async () => {
            try {
              await apiInstance.post(`students/${row.original.id}/tag/`, {
                tag: 'highlight',
                context: tableConfig.context
              });
              await fetchData();
            } catch (error) {
              console.log(JSON.stringify(error));
            }
          }
        });
      } else if (tags.length > 0 && tableConfig.context === 'reenrollment') {
        const highlightTag = tags.find((tag) => tag.tag === 'highlight');
        actionChoices.push({
          label: 'Remover destaque',
          onClick: async () => {
            try {
              await apiInstance.delete(`students/${row.original.id}/tag/${highlightTag.id}/`, {
                data: {
                  tag: 'highlight',
                  context: tableConfig.context
                }
              });
              await fetchData();
            } catch (error) {
              console.log(JSON.stringify(error));
            }
          }
        });
      }
      return (
        actionChoices.map((action, index) => (
          <Menu.Item key={index} onClick={action.onClick}>{action.label}</Menu.Item>
        ))
      )
    },
    // Row detail - disabled since we're using modal now
    enableExpandAll: false,
      });

      return (
    <>
    <MantineReactTable 
      table={table}
      style={{border: '0px !important'}}
    />
      
      {/* Student Detail Modal */}
      {selectedRow && (
        <StudentDetailModal
          opened={modalOpened}
          onClose={handleModalClose}
          row={selectedRow}
          enrollments={selectedRow.original.enrollments.filter((enrollment) => {
            if (selectedRow.original.status === 'active' && enrollment.status !== 'active') {
              return false;
            }
            return true;
          })}
          tableConfig={tableConfig}
          handleReenrollmentStatusUpdate={handleReenrollmentStatusUpdate}
          handleLogUpdate={handleLogUpdate}
        />
      )}
      
      {/* Student Logs Modal */}
      {selectedStudentForLogs && (
        <StudentLogs
          opened={logsModalOpened}
          onClose={handleLogsModalClose}
          studentId={selectedStudentForLogs.id}
          studentName={selectedStudentForLogs.name}
                          context={tableConfig.context}
        />
      )}
    </>
  );
};


export default StudentsTable;
